import React, { useEffect, useCallback, useMemo, forwardRef, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, ImageOverlay } from 'react-leaflet';
import L from 'leaflet';
import MapStateSynchronizer from './MapStateSynchronizer.js';
import { LEAFLET_CRS, NORMAL_OPACITY, FADED_OPACITY } from './mapConstants.js';
import MarkerInfoPanel from './MarkerInfoPanel.js';
import PixiOverlay from "react-leaflet-pixi-overlay";
import { generatePopupContentForMarker, generateModuleNameSvg, initializeCollapsiblePools } from './mapLayoutService.js';

import 'leaflet/dist/leaflet.css';

const MapDisplay = forwardRef(({
    mapKey, moduleOverlays, iconsToPlot, initialBounds, isolatedModule,
    zoom, setZoom, updateVersion,
    lootDataByGrade,
    ensureLootDataIsLoaded,
    isLoadingLoot,
    mainNavCollapsed,
    showModuleNamesOnMap,
}, ref) => {
    const mapInstance = useRef(null);
    const [selectedMarkerDataForPanel, setSelectedMarkerDataForPanel] = useState(null); 
    const [currentLeafletPopup, setCurrentLeafletPopup] = useState(null);

    const boundsToFit = useMemo(() => {
        let targetBounds = null;
        if (isolatedModule) {
            const targetOverlay = moduleOverlays.find(ov => ov.moduleName === isolatedModule);
            if (targetOverlay?.bounds) {
                try {
                    targetBounds = L.latLngBounds(targetOverlay.bounds);
                    if (!targetBounds.isValid()) targetBounds = null;
                } catch (e) { targetBounds = null; }
            }
        }
        if (!targetBounds?.isValid() && initialBounds?.isValid()) {
            targetBounds = initialBounds;
        }
        return targetBounds;
    }, [isolatedModule, moduleOverlays, initialBounds]);

    const handleCloseActions = useCallback(() => {
        setSelectedMarkerDataForPanel(null);
        if (currentLeafletPopup) {
            currentLeafletPopup.remove();
            setCurrentLeafletPopup(null);
        }
    }, [currentLeafletPopup]);

    const handleMarkerClick = useCallback((clickedMarkerObject) => {
        setSelectedMarkerDataForPanel(clickedMarkerObject);

        if (currentLeafletPopup) {
            currentLeafletPopup.remove();
        }

        if (mapInstance.current && clickedMarkerObject && clickedMarkerObject.position) {
            const specificLootDataForMarkerGrade = lootDataByGrade?.get(clickedMarkerObject.dungeonGradeKey);
            
            const popupContentObject = generatePopupContentForMarker(
                clickedMarkerObject.LocalizedString, 
                specificLootDataForMarkerGrade,
                clickedMarkerObject.zIndexValue,
                clickedMarkerObject.moduleName,
                clickedMarkerObject.spawn_chances,
                clickedMarkerObject.gameMode,
                clickedMarkerObject.entityGroupId,
                clickedMarkerObject.dungeonGradeKey,
                clickedMarkerObject.associated_lootdrop_ids,
                clickedMarkerObject.LocalizedString,
                undefined, 
                clickedMarkerObject.moduleDisplayName,
                clickedMarkerObject.object_name,
            );

            let leafletPopupLootHtml = '';
            if (popupContentObject.lootData.tabs && popupContentObject.lootData.tabs.length > 0) {
                const firstTab = popupContentObject.lootData.tabs[0];
                leafletPopupLootHtml += `<div class="tab-content-placeholder" style="max-height: 150px; overflow-y: auto; font-size: 0.9em;"><h4>${firstTab.tabLabel}</h4>`; // Added style for scroll
                if (firstTab.categorizedPools && firstTab.categorizedPools.length > 0) {
                    const firstCategory = firstTab.categorizedPools[0];
                    leafletPopupLootHtml += `<h5 class="loot-category-header" style="font-size: 0.95em; margin-bottom: 5px;">${firstCategory.categoryLabel}</h5>`; // Style for category header
                    const firstPoolHtml = firstCategory.poolsHtmlForCategory.match(/<div class="drop-check-pool-container.*?<\/div>/s);
                    if (firstPoolHtml) {
                        leafletPopupLootHtml += firstPoolHtml[0];
                    } else {
                         leafletPopupLootHtml += `<div><i>See panel for full details.</i></div>`;
                    }
                } else if (firstTab.poolCount > 0) {
                     leafletPopupLootHtml += `<div><i>Contains ${firstTab.poolCount} loot pool(s). See panel for details.</i></div>`;
                } else if (firstTab.tabSpecificNoItemsMessage && firstTab.tabSpecificNoItemsMessage.trim() !== '') {
                    leafletPopupLootHtml += firstTab.tabSpecificNoItemsMessage;
                }
                 else {
                    leafletPopupLootHtml += `<div><i>No specific loot pools in this tab. See panel for details.</i></div>`;
                }
                leafletPopupLootHtml += `</div>`;
            } else if (popupContentObject.lootData.noLootMessageHtmlIfAny) {
                leafletPopupLootHtml = popupContentObject.lootData.noLootMessageHtmlIfAny;
            } else {
                 leafletPopupLootHtml = `<div><i>See panel for loot details.</i></div>`;
            }
            
            const fullPopupHtml = (popupContentObject.mainHeaderHtml || '') +
                                  (popupContentObject.spawnChancesHtml || '') +
                                  (popupContentObject.separatorHtml || '') +
                                  (popupContentObject.lootData.overallLootTitleHtml || '') +
                                  leafletPopupLootHtml;


            const popup = L.popup({ className: 'custom-leaflet-popup', autoClose: true, closeOnClick: true, maxHeight: 300 }) 
                .setLatLng(clickedMarkerObject.position)
                .setContent(fullPopupHtml)
                .openOn(mapInstance.current);

            popup.on('remove', () => {
                if (currentLeafletPopup === popup) {
                    setCurrentLeafletPopup(null);
                }
            });
            setCurrentLeafletPopup(popup);

            setTimeout(() => {
                if (popup.getElement()) {
                    if (popup.getElement().querySelector('.collapsible-trigger')) {
                        initializeCollapsiblePools(popup.getElement());
                    }
                }
            }, 0);
        }
    }, [currentLeafletPopup, lootDataByGrade, mapInstance]);


    const markersToDisplay = useMemo(() => {
        let allDisplayableMarkers = [];
        let baseFilteredMarkers = iconsToPlot || [];

        if (isolatedModule) {
            baseFilteredMarkers = (iconsToPlot || []).filter(marker => marker.moduleName === isolatedModule);
        }

        const validAndStructureCheckedMarkers = baseFilteredMarkers.filter(m =>
            m && typeof m.id === 'string' &&
            Array.isArray(m.position) && m.position.length === 2 && !isNaN(m.position[0]) && !isNaN(m.position[1]) &&
            typeof m.customIconSvg === 'string' &&
            typeof m.object_name === 'string' && 
            typeof m.LocalizedString === 'string' &&
            typeof m.moduleName === 'string' &&
            typeof m.gameMode === 'string' &&
            Array.isArray(m.associated_lootdrop_ids) &&
            typeof m.dungeonGradeKey === 'string'
       
        );

        allDisplayableMarkers = validAndStructureCheckedMarkers.map(m => ({
            id: m.entityGroupId || m.id,
            iconId: m.entityGroupId || m.id,
            customIcon: m.customIconSvg,
            position: m.position,
            onClick: () => handleMarkerClick(m), 
            zIndex: m.zIndexValue !== undefined ? m.zIndexValue : 0,
            interactive: true,
        }));

        if (showModuleNamesOnMap) {
            const moduleNameMarkers = moduleOverlays.map(overlay => {
                if (!overlay.bounds || !overlay.moduleName) return null;
                let bounds;
                try {
                    bounds = L.latLngBounds(overlay.bounds);
                    if (!bounds.isValid()) return null;
                } catch (e) { return null; }

                const center = bounds.getCenter();
                const moduleNameSvgIcon = generateModuleNameSvg(overlay.moduleDisplayName, overlay.moduleName, { currentMapType: mapKey });
                return {
                    id: `module-name-marker-${overlay.moduleName}`,
                    iconId: `module-name-svg-${overlay.moduleName}`, 
                    customIcon: moduleNameSvgIcon,
                    position: [center.lat - 300, center.lng], 
                    interactive: false,
                    zIndex: 1, 
                };
            }).filter(Boolean);
            allDisplayableMarkers = allDisplayableMarkers.concat(moduleNameMarkers);
        }
        return allDisplayableMarkers;
    }, [iconsToPlot, isolatedModule, handleMarkerClick, showModuleNamesOnMap, moduleOverlays, mapKey]);


    const handleMapCreated = useCallback((instance) => {
        mapInstance.current = instance;
        if (typeof ref === 'function') {
            ref(instance);
        } else if (ref) {
            ref.current = instance;
        }
        setTimeout(() => {
            if (instance && instance.getContainer() && document.body.contains(instance.getContainer())) {
                try { instance.invalidateSize({ animate: false }); } catch (e) { /* ignore */ }
            }
        }, 100);
    }, [ref]);

    useEffect(() => {
        const map = mapInstance.current;
        if (map) {
            const onMapClickHandler = (e) => {
                const target = e.originalEvent.target;
                if (target === map.getContainer() || 
                    (!L.DomUtil.hasClass(target, 'leaflet-marker-icon') && !L.DomUtil.hasClass(target, 'leaflet-popup-content') && !target.closest('.leaflet-popup-content-wrapper') && !target.closest('.marker-info-panel'))) {
                    handleCloseActions();
                }
            };
            map.on('click', onMapClickHandler);
            return () => {
                map.off('click', onMapClickHandler);
            };
        }
    }, [mapInstance, handleCloseActions]); 

    useEffect(() => {
 
        return () => {
            if (currentLeafletPopup) {
                currentLeafletPopup.remove();
            }
        };
    }, [mapKey, currentLeafletPopup]); 

    useEffect(() => {
        if (selectedMarkerDataForPanel && currentLeafletPopup && currentLeafletPopup.isOpen() && mapInstance.current) {
            const specificLootDataForMarkerGrade = lootDataByGrade?.get(selectedMarkerDataForPanel.dungeonGradeKey);
            const popupContentObject = generatePopupContentForMarker(
                selectedMarkerDataForPanel.LocalizedString, 
                specificLootDataForMarkerGrade,
                selectedMarkerDataForPanel.zIndexValue,
                selectedMarkerDataForPanel.moduleName,
                selectedMarkerDataForPanel.spawn_chances, 
                selectedMarkerDataForPanel.gameMode,
                selectedMarkerDataForPanel.entityGroupId,
                selectedMarkerDataForPanel.dungeonGradeKey,
                selectedMarkerDataForPanel.associated_lootdrop_ids,
                selectedMarkerDataForPanel.LocalizedString,
                undefined, 
                selectedMarkerDataForPanel.moduleDisplayName,
                selectedMarkerDataForPanel.object_name,
            );
            
        
            let newLeafletPopupLootHtml = '';
            if (popupContentObject.lootData.tabs && popupContentObject.lootData.tabs.length > 0) {
                const firstTab = popupContentObject.lootData.tabs[0];
                newLeafletPopupLootHtml += `<div class="tab-content-placeholder" style="max-height: 150px; overflow-y: auto; font-size: 0.9em;"><h4>${firstTab.tabLabel}</h4>`;
                if (firstTab.categorizedPools && firstTab.categorizedPools.length > 0) {
                    const firstCategory = firstTab.categorizedPools[0];
                     newLeafletPopupLootHtml += `<h5 class="loot-category-header" style="font-size: 0.95em; margin-bottom: 5px;">${firstCategory.categoryLabel}</h5>`;
                     const firstPoolHtml = firstCategory.poolsHtmlForCategory.match(/<div class="drop-check-pool-container.*?<\/div>/s);
                     if (firstPoolHtml) {
                        newLeafletPopupLootHtml += firstPoolHtml[0];
                     } else {
                        newLeafletPopupLootHtml += `<div><i>See panel for full details.</i></div>`;
                     }
                } else if (firstTab.poolCount > 0) {
                     newLeafletPopupLootHtml += `<div><i>Contains ${firstTab.poolCount} loot pool(s). See panel for details.</i></div>`;
                } else if (firstTab.tabSpecificNoItemsMessage && firstTab.tabSpecificNoItemsMessage.trim() !== '') {
                    newLeafletPopupLootHtml += firstTab.tabSpecificNoItemsMessage;
                }
                 else {
                    newLeafletPopupLootHtml += `<div><i>No specific loot pools in this tab. See panel for details.</i></div>`;
                }
                newLeafletPopupLootHtml += `</div>`;
            } else if (popupContentObject.lootData.noLootMessageHtmlIfAny) {
                newLeafletPopupLootHtml = popupContentObject.lootData.noLootMessageHtmlIfAny;
            } else {
                newLeafletPopupLootHtml = `<div><i>See panel for loot details.</i></div>`;
            }

            const newFullPopupHtml = (popupContentObject.mainHeaderHtml || '') +
                                   (popupContentObject.spawnChancesHtml || '') +
                                   (popupContentObject.separatorHtml || '') +
                                   (popupContentObject.lootData.overallLootTitleHtml || '') +
                                   newLeafletPopupLootHtml;

            currentLeafletPopup.setContent(newFullPopupHtml);
            setTimeout(() => {
                if (currentLeafletPopup.getElement()) {
                     if (currentLeafletPopup.getElement().querySelector('.collapsible-trigger')) {
                        initializeCollapsiblePools(currentLeafletPopup.getElement());
                    }
                }
            }, 0);
        }
    }, [selectedMarkerDataForPanel, lootDataByGrade, currentLeafletPopup]); 


    return (
        <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
            <MapContainer
                key={mapKey}
                whenCreated={handleMapCreated}
                center={[0, 0]} 
                zoom={zoom}
                crs={LEAFLET_CRS}
                attributionControl={false}
                zoomControl={false}
                minZoom={-10} 
                maxZoom={10}  
                zoomSnap={0.1}
                style={{ width: '100%', height: '100%', backgroundColor: '#1E1C18' }}
                scrollWheelZoom={true}
                dragging={true}
            >
                {moduleOverlays.map((overlay) => {
                    const opacity = isolatedModule === null ? NORMAL_OPACITY : (overlay.moduleName === isolatedModule ? NORMAL_OPACITY : FADED_OPACITY);
                    let isValidBounds = false;
                    try {
                        if (overlay.bounds) {
                            const bounds = L.latLngBounds(overlay.bounds);
                            isValidBounds = bounds.isValid();
                        }
                    } catch (e) { /* ignore */ }

                    if (!isValidBounds) return null; 

                    return (
                        <ImageOverlay
                            key={overlay.moduleName}
                            url={overlay.url}
                            bounds={overlay.bounds}
                            opacity={opacity}
                            pane="tilePane" 
                            interactive={false}
                        />
                    );
                })}
                <MapStateSynchronizer
                    targetZoom={zoom}
                    onZoomChange={setZoom}
                    triggerFitBounds={updateVersion} 
                    activeBounds={boundsToFit}
                    isolatedModule={isolatedModule}
                />

                <PixiOverlay markers={markersToDisplay} />

            </MapContainer>

            {selectedMarkerDataForPanel && (
                <MarkerInfoPanel
                    markerData={selectedMarkerDataForPanel}
                    onClose={handleCloseActions} 
                    lootDataByGrade={lootDataByGrade}
                    ensureLootDataIsLoaded={ensureLootDataIsLoaded}
                    isLoadingLoot={isLoadingLoot}
                />
            )}
        </div>
    );
});
MapDisplay.displayName = 'MapDisplay';

MapDisplay.propTypes = {
    mapKey: PropTypes.string.isRequired,
    moduleOverlays: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string.isRequired,
        bounds: PropTypes.array.isRequired, 
        moduleName: PropTypes.string.isRequired,
        moduleDisplayName: PropTypes.string
    })).isRequired,
    iconsToPlot: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        position: PropTypes.arrayOf(PropTypes.number).isRequired,
        customIconSvg: PropTypes.string.isRequired,
        object_name: PropTypes.string.isRequired,
        gameMode: PropTypes.string.isRequired,
        associated_lootdrop_ids: PropTypes.arrayOf(PropTypes.string).isRequired,
        dungeonGradeKey: PropTypes.string.isRequired,
        LocalizedString: PropTypes.string, 
        moduleName: PropTypes.string.isRequired,
        moduleDisplayName: PropTypes.string,
        zIndexValue: PropTypes.number,
        spawn_chances: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.oneOf([null]) 
        ]),
        entityGroupId: PropTypes.string,
    })).isRequired,
    initialBounds: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(L.LatLngBounds)]),
    isolatedModule: PropTypes.string,
    zoom: PropTypes.number.isRequired,
    setZoom: PropTypes.func.isRequired,
    updateVersion: PropTypes.number.isRequired, 
    lootDataByGrade: PropTypes.instanceOf(Map),
    ensureLootDataIsLoaded: PropTypes.func.isRequired,
    isLoadingLoot: PropTypes.bool,
    mainNavCollapsed: PropTypes.bool,
    showModuleNamesOnMap: PropTypes.bool,
};

MapDisplay.defaultProps = {
    initialBounds: null,
    isolatedModule: null,
    lootDataByGrade: new Map(),
    isLoadingLoot: false,
    mainNavCollapsed: false,
    showModuleNamesOnMap: false,
};

export default MapDisplay;